import store from '@/store';
import { setCurrentSpace, setPersonalUserId, resetSpace, TeamSpaceInfo, TeamRole } from '@/store/slices/spaceSlice';
import { teamService } from '@/services/amai/team.service';
import {
  getToken,
  setToken,
  getPersonalTokenBackup,
  setPersonalTokenBackup,
  clearPersonalTokenBackup,
} from '@/utils/auth';
import { apiCache } from '@/utils/cache/apiCache';
import { eventBus } from '@/utils/eventBus';
import { broadcastSpaceSwitch } from '@/utils/spaceSyncChannel';
import { resolveBrand } from '@/config/brand';

/**
 * 空间切换编排（V0.1 团队协作）
 *
 * 切换 = 换 token：POST /auth/token/exchange 换发目标空间 token → 写 anmo_ai_token →
 * 清缓存/刷新会员状态 → 通知全局 → 跳首页。数据隔离由后端按 token 身份实现。
 *
 * 时序敏感点：
 * - 先换 token 再 dispatch（切换期间任何请求都应带新 token）
 * - apiCache.clear() 必须做：requestKey 无空间维度，/user/package 5min 缓存等会串空间
 * - refreshIsVip() 必须做：useIsVip 是 30s TTL 模块级单例，不清会沿用上个空间的判定
 */
class SpaceManager {
  /** 切换进行中标记（防连点竞态；V0.1 不做在途请求硬阻断） */
  private switching = false;

  get isSwitching(): boolean {
    return this.switching;
  }

  /** 个人 token 备份：首次从个人切往团队时保存，供团队 token 失效回退 */
  private ensurePersonalTokenBackup(): void {
    if (getPersonalTokenBackup()) return;
    const token = getToken();
    if (token && !token.startsWith('t_')) {
      setPersonalTokenBackup(token);
    }
  }

  /**
   * 切换到目标空间
   * @param target 目标空间（null = 个人空间）
   * @param navigate 跳转函数（AccountMenu 调用时传入 react-router 的 navigate）
   * @returns 是否切换成功
   */
  async switchToSpace(
    target: TeamSpaceInfo | null,
    navigate: (to: string, opts?: { replace?: boolean }) => void
  ): Promise<boolean> {
    if (this.switching) {
      console.warn('[SpaceManager] 切换进行中，忽略本次触发');
      return false;
    }
    this.switching = true;
    try {
      const targetUserId = target ? target.teamId : await this.getPersonalUserId();
      if (!targetUserId) {
        console.error('[SpaceManager] 无法确定目标账户 user_id，切换中止');
        return false;
      }

      // 1. 换 token（团队切回个人同样走交换接口）
      const response = await teamService.exchangeToken(targetUserId);
      if (response.code !== 0 || !response.data?.token) {
        console.error('[SpaceManager] 交换 token 失败:', response.message);
        return false;
      }

      // 2. 备份个人 token（团队 token 写入前）+ 写入新 token
      if (target) {
        this.ensurePersonalTokenBackup();
      } else {
        clearPersonalTokenBackup();
      }
      setToken(response.data.token);

      // 2.1 持久化交换响应携带的个人 user_id（切回个人时交换 target 用，防 account_list 未拉到时无 id 可换）
      if (response.data.personal_user_id) {
        this.recordPersonalUserId(response.data.personal_user_id);
      }

      // 3. 更新空间状态（persist 持久化，刷新后保持）
      store.dispatch(setCurrentSpace(target));

      // 4. 清缓存：requestKey 无空间维度，全部作废防串空间
      apiCache.clear();

      // 5. 刷新 VIP 单例 + 清跨页会话（防个人会话带进团队）+ 广播
      const { refreshIsVip } = await import('@/hooks/useIsVip');
      refreshIsVip();
      const { clearSession } = await import('@/store/slices/crossPageSessionSlice');
      store.dispatch(clearSession());
      eventBus.emit('spaceChanged', target);

      // 5.1 SharedWorker 广播给其他标签（canvas/director 等页弹「会话过期」提示）
      const state = store.getState() as { space: { personalUserId: string | null } };
      broadcastSpaceSwitch(target ? target.teamId : state.space.personalUserId ?? null, target?.teamName ?? null);

      // 6. 跳首页（初始页面）
      navigate(resolveBrand().defaultRoute || '/home', { replace: true });
      return true;
    } catch (error) {
      console.error('[SpaceManager] 切换空间失败:', error);
      return false;
    } finally {
      this.switching = false;
    }
  }

  /** 获取个人账户 user_id：优先 redux 持久化的 personalUserId，其次 user_info（切换列表里也有） */
  private async getPersonalUserId(): Promise<string | null> {
    const state = store.getState() as { space: { personalUserId: string | null } };
    if (state.space.personalUserId) return state.space.personalUserId;

    // 从账户列表兜底取（当前在团队空间时 user_info 是团队身份，不能直接用）
    try {
      const response = await teamService.getAccountList();
      if (response.code === 0 && response.data?.personal_account?.user_id) {
        const personalUserId = response.data.personal_account.user_id;
        store.dispatch(setPersonalUserId(personalUserId));
        return personalUserId;
      }
    } catch (error) {
      console.error('[SpaceManager] 获取账户列表失败:', error);
    }
    return null;
  }

  /** 记录个人 user_id（登录/profile 拉取后调用，画布 creator_user_id 比对用） */
  recordPersonalUserId(userId: string | null | undefined): void {
    if (!userId) return;
    // 防脏写：团队空间下若把 teamId 误当个人 id 记入（如 profile 在团队 token 下语义漂移），
    // creator_user_id 比对会永久错判 → 自己的画布被误判只读（小墨等编辑全禁）。宁拒录勿录错。
    const state = store.getState() as { space: { current: TeamSpaceInfo | null } };
    if (state.space.current && userId === state.space.current.teamId) {
      console.warn('[SpaceManager] 拒绝把 teamId 记为 personalUserId:', userId);
      return;
    }
    store.dispatch(setPersonalUserId(userId));
  }

  /** 退出登录时重置空间状态 */
  reset(): void {
    store.dispatch(resetSpace());
    clearPersonalTokenBackup();
  }

  /**
   * 校验当前空间有效性（P4）：启动时比对 account_list，
   * 当前 teamId 不在列表 → 已被移出/解散，回落个人空间
   */
  async validateCurrentSpace(navigate: (to: string, opts?: { replace?: boolean }) => void): Promise<boolean> {
    const state = store.getState() as { space: { current: TeamSpaceInfo | null } };
    const current = state.space.current;
    if (!current) return true; // 个人空间无需校验

    try {
      const response = await teamService.getAccountList();
      if (response.code !== 0 || !response.data) return true; // 接口异常不误伤
      const exists = response.data.team_account_list?.some(t => t.user_id === current.teamId);
      if (!exists) {
        console.warn('[SpaceManager] 当前团队不在账户列表中，回落个人空间（被移出或已解散）');
        await this.switchToSpace(null, navigate);
        eventBus.emit('spaceInvalid', { teamId: current.teamId });
        return false;
      }
      return true;
    } catch {
      return true; // 网络异常不误伤
    }
  }
}

export const spaceManager = new SpaceManager();
export { TeamRole };
